import { useEffect, useRef, useState } from "react";
import useMediaQuery from "../hooks/useMediaQuery";
import { useCart } from "../context/CartContext";
import products from "../data/products.json";
import MindstateVinyl from "../assets/optimized/mindstate-vinyl.webp";
import MeltTee from "../assets/optimized/melt-tee.webp";
import EchoTee from "../assets/optimized/echo-tee.webp";
import PsychBlast from "../assets/optimized/psych-blast-tee.webp";
import GodsCassette from "../assets/optimized/gods-clods-cassette.webp";
import AnotherMind from "../assets/optimized/another-mind-cd.webp";
// import Stickers from "../assets/stickers.jpg";
import HatinYouFinal from "../assets/optimized/hatin-you-7in.webp";
import SelfTitledCover from "../assets/optimized/self-titled-cover.webp";
import LiveShows from "../components/LiveShows";
import LiteYouTube from "../components/LiteYouTube";

import { motion } from "framer-motion";
// import YouTube from "react-youtube";

const ScrollArrow = ({ direction, onClick }) => (
  <button
    onClick={onClick}
    aria-label={direction === "left" ? "Scroll videos left" : "Scroll videos right"}
    className="absolute top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-coolDark text-coolPaper flex items-center justify-center shadow-lg hover:bg-rust transition-colors"
    style={direction === "left" ? { left: "-0.5rem" } : { right: "-0.5rem" }}
  >
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      {direction === "left" ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
    </svg>
  </button>
);

const VideoScrollRow = ({ videoIds }) => {
  const scrollRef = useRef(null);

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" });
  };

  return (
    <div className="relative w-full max-w-5xl mx-auto px-6">
      <ScrollArrow direction="left" onClick={() => scroll(-1)} />
      <div
        ref={scrollRef}
        className="flex gap-5 overflow-x-auto scroll-smooth pb-2"
      >
        {videoIds.map((videoId) => (
          <LiteYouTube
            key={videoId}
            videoId={videoId}
            className="shrink-0"
            width={340}
            height={191}
          />
        ))}
      </div>
      <ScrollArrow direction="right" onClick={() => scroll(1)} />
    </div>
  );
};

const PRODUCT_IMAGES = {
  "self-titled-bloom-splatter-vinyl": SelfTitledCover,
  "hatin-you-7in": HatinYouFinal,
  "mindstate-vinyl": MindstateVinyl,
  "gods-clods-cassette": GodsCassette,
  "another-mind-cd": AnotherMind,
  "melt-tee": MeltTee,
  "echo-tee": EchoTee,
  "psych-blast-tee": PsychBlast,
};

const MerchCard = ({ product, onQuickView }) => (
  <div
    className="group relative bg-black/30 rounded cursor-pointer overflow-hidden transition-transform duration-300 hover:-translate-y-1"
    onClick={() => onQuickView(product)}
  >
    <div className="relative aspect-square overflow-hidden bg-black">
      <img
        src={PRODUCT_IMAGES[product.id]}
        alt={product.name}
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
      />
      <span className="absolute left-1/2 bottom-3 -translate-x-1/2 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-200 bg-rust text-coolPaper text-xs uppercase tracking-wider font-monda font-bold px-4 py-2 rounded-full whitespace-nowrap">
        Quick View
      </span>
    </div>
    <div className="p-3">
      <h3 className="font-monda text-sm text-coolPaper">{product.name}</h3>
    </div>
  </div>
);

const QuickViewModal = ({ product, onClose }) => {
  const { addItem } = useCart();
  const [size, setSize] = useState(product?.sizes ? product.sizes[0] : undefined);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setSize(product?.sizes ? product.sizes[0] : undefined);
    setQty(1);
  }, [product]);

  if (!product) return null;

  const handleAddToCart = () => {
    addItem(product.id, size, qty);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/70"
      onClick={onClose}
    >
      <div
        className="bg-coolDark w-full max-w-2xl grid grid-cols-1 md:grid-cols-2 rounded overflow-hidden max-h-[88vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-black aspect-square md:aspect-auto">
          <img
            src={PRODUCT_IMAGES[product.id]}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="p-6 relative font-monda text-coolPaper overflow-y-auto">
          <button
            className="absolute top-4 right-4 text-2xl leading-none text-coolPaper/70"
            onClick={onClose}
          >
            &times;
          </button>
          <h3 className="text-xl font-semibold mb-1 pr-8">{product.name}</h3>
          <p className="text-rust text-lg mb-6">${product.priceUSD.toFixed(2)}</p>

          {product.sizes && (
            <>
              <div className="text-xs uppercase tracking-wider text-coolPaper/60 font-bold mb-2">
                Size
              </div>
              <div className="flex gap-2 mb-6">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    className={`w-10 h-10 rounded-full border text-sm font-bold transition-colors ${
                      size === s
                        ? "bg-rust border-rust text-coolPaper"
                        : "border-coolPaper/30 text-coolPaper hover:border-rust"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </>
          )}

          <div className="text-xs uppercase tracking-wider text-coolPaper/60 font-bold mb-2">
            Quantity
          </div>
          <div className="flex items-center gap-3 mb-7">
            <button
              className="w-8 h-8 rounded-full border border-coolPaper/30 hover:border-rust"
              onClick={() => setQty((q) => Math.max(1, q - 1))}
            >
              &minus;
            </button>
            <span className="font-bold w-4 text-center">{qty}</span>
            <button
              className="w-8 h-8 rounded-full border border-coolPaper/30 hover:border-rust"
              onClick={() => setQty((q) => q + 1)}
            >
              +
            </button>
          </div>

          <button
            className="w-full bg-rust hover:bg-rustDeep text-coolPaper font-bold uppercase tracking-wide text-sm py-3 rounded"
            onClick={handleAddToCart}
          >
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

const SelfTitledFeature = () => {
  const { addItem } = useCart();

  return (
    <div className="flex flex-col md:flex-row items-center md:items-stretch gap-10 md:gap-16 mx-6 md:mx-10">
      <div className="w-full md:w-1/2 aspect-[8/5] md:aspect-auto bg-black shrink-0 overflow-hidden">
        <img
          src={SelfTitledCover}
          alt="Strange Lot Self-Titled"
          className="w-full h-full object-contain"
        />
      </div>
      <div className="flex-1 flex flex-col justify-center font-monda text-coolInk text-center md:text-left">
        <p className="text-rustDeep text-xs uppercase tracking-widest font-bold mb-2">
          New Release
        </p>
        <p className="font-integralCF text-4xl md:text-6xl mb-2">STRANGE LOT</p>
        <p className="text-rustDeep font-bold uppercase tracking-wide text-sm md:text-base mb-4">
          New LP. Out now on Greenway Records.
        </p>
        <p className="text-sm text-coolInk/70 mb-1">Bloom Splatter Vinyl. Edition of 200.</p>
        <p className="text-sm text-coolInk/70 mb-6">
          Ships in 5-10 business days (US).
        </p>
        <button
          className="bg-coolDark hover:bg-rust text-coolPaper font-bold uppercase tracking-wide text-sm py-3 px-6 rounded transition-colors self-center md:self-start"
          onClick={() => addItem("self-titled-bloom-splatter-vinyl")}
        >
          Order Vinyl
        </button>
      </div>
    </div>
  );
};

// Verified directly from the rendered Bandsintown widget's own follow button
// href during DOM inspection - rebuilt as our own link so it can be
// positioned independently of the widget instead of embedding a second
// widget (this repo has previously hit bugs from duplicate widget instances).
const FOLLOW_URL =
  "https://www.bandsintown.com/artist-subscribe/10733964-strange-lot?app_id=996ff3e9d664fe9051bcd36a756f22b4&utm_medium=web&utm_source=widget&utm_campaign=track";

const Store = () => {
  const isAboveLarge = useMediaQuery("(min-width:900px)");
  const { items, subtotalUSD, clearCart } = useCart();
  const [checkoutStatus, setCheckoutStatus] = useState(null);
  const [quickViewProduct, setQuickViewProduct] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("checkout");
    if (status === "success") {
      // Report the sale to the Meta Pixel before the cart is cleared, since
      // the cart is the only place the order contents live client-side. An
      // empty cart means this is a reload of the success URL, so skip it to
      // avoid double-counting. eventID is the Stripe session id so a future
      // server-side Conversions API event can be deduplicated against this one.
      if (window.fbq && items.length > 0) {
        window.fbq(
          "track",
          "Purchase",
          {
            value: subtotalUSD,
            currency: "USD",
            content_ids: items.map((i) => i.productId),
            content_type: "product",
            num_items: items.reduce((sum, i) => sum + i.qty, 0),
          },
          { eventID: params.get("session_id") || undefined }
        );
      }
      clearCart();
      setCheckoutStatus("success");
    } else if (status === "cancel") {
      setCheckoutStatus("cancel");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Loaded here (after the bit-widget-initializer div below has mounted) rather
    // than as a <script defer> in index.html - a deferred script can run before
    // React mounts this div into the DOM, so Bandsintown's one-time scan for
    // .bit-widget-initializer finds nothing and the widget never initializes.
    if (document.querySelector('script[src="https://widgetv3.bandsintown.com/main.min.js"]')) {
      return;
    }
    const script = document.createElement("script");
    script.src = "https://widgetv3.bandsintown.com/main.min.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  const videoIds = [
    "Ln3P2uy_qmo",
    "KZpUSuDCPAQ",
    "kgb4VaKufME",
    "pyxxpMbP2Hc",
    "iI1IgKIEpVI",
    "kHt_STv-ROg",
    "CmkshpeZRq8",
    "2hqqBRUxqqQ",
    "Odqt51IXkQ8",
    "qhszP41dtPw",
  ];
  const heroVideoId = videoIds[0];
  const scrollVideoIds = videoIds.slice(1);

  return (
    <section className="pt-0 pb-0">
      {checkoutStatus === "success" && (
        <div className="bg-rust text-coolPaper font-monda text-center p-3">
          Thanks for your order! A confirmation email is on its way.
        </div>
      )}
      {checkoutStatus === "cancel" && (
        <div className="bg-coolDark text-coolPaper font-monda text-center p-3">
          Checkout was cancelled. Your cart is still saved.
        </div>
      )}

      {/* KEXP VIDEO - ABOVE THE HERO/SELF-TITLED FEATURE BELOW */}
      <div className="w-full bg-coolDark flex justify-center py-8">
        <div className="w-full max-w-4xl aspect-video px-4">
          <LiteYouTube videoId="u4lhwuSHaeg" className="w-full h-full" />
        </div>
      </div>

      <div className="w-full bg-coolLight py-14 md:py-20">
        <motion.div
          className="md:w-full"
          initial="hidden"
          whileInView={isAboveLarge ? "visible" : null}
          viewport={{ once: true, amount: isAboveLarge ? 0.5 : 1 }}
          transition={{ duration: isAboveLarge ? 0.3 : 0 }}
          variants={
            isAboveLarge
              ? { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } }
              : {}
          }
        >
          <SelfTitledFeature />
        </motion.div>
        <p className="font-monda text-sm text-coolInk/70 text-center max-w-2xl mx-auto mt-12 px-6">
          STRANGE LOT is a psych rock band from Austin, Texas. Their self-titled
          album, out now on Greenway Records, was produced by Alex Maas of The
          Black Angels, mixed by Jim Eno of Spoon and mastered by Howie Weinberg.
        </p>
      </div>

      {/* Nav STORE link and the side dot scroll here, not to the top of this
          section - the KEXP video and LP feature sit above the store grid. */}
      <div
        id="store"
        className="w-full bg-coolDark py-14 md:py-20 flex flex-col items-center"
      >
        <h2
          className={`font-integralCF md:text-5xl w-full md:text-center mb-8 text-coolPaper ${
            isAboveLarge ? "text-5xl" : "text-4xl"
          } text-center`}
        >
          <span className="border-b-2 inline-block">STORE</span>
        </h2>

        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-5 max-w-5xl w-[90%] mx-auto mt-8"
          initial="hidden"
          whileInView={isAboveLarge ? "visible" : null}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.4 }}
          variants={
            isAboveLarge
              ? { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } }
              : {}
          }
        >
          {products
            .filter((p) => p.id !== "self-titled-bloom-splatter-vinyl")
            .map((product) => (
              <MerchCard
                key={product.id}
                product={product}
                onQuickView={setQuickViewProduct}
              />
            ))}
        </motion.div>
      </div>

      <QuickViewModal
        product={quickViewProduct}
        onClose={() => setQuickViewProduct(null)}
      />

      {/* *****VIDEO SECTION BELOW***** */}
      <div
        id="video"
        className="flex flex-col items-center bg-coolLight text-coolInk py-14 md:py-20 align-top md:flex-col md:justify-start md:gap-16 sm:align-top"
      >
        <motion.div className="md:w-full md:text-center">
          <h2
            className={`font-gridstar font-semibold mb-10 ${
              isAboveLarge ? "text-7xl" : "text-4xl mt-5"
            } text-center`}
          >
            <span className="border-b-2 inline-block">VIDEO</span>
          </h2>
        </motion.div>
        {/* ^^^^^ VIDEO TITLE ^^^ */}

        <LiteYouTube
          videoId={heroVideoId}
          className={isAboveLarge ? "mb-10" : "h-[236px] w-[340px] mb-8"}
          width={isAboveLarge ? 960 : undefined}
          height={isAboveLarge ? 540 : undefined}
        />

        <VideoScrollRow videoIds={scrollVideoIds} />
      </div>

      {/* *****LIVE SECTION BELOW***** */}
      <div
        id="live"
        className="flex flex-col items-center bg-coolDark text-coolPaper py-14 md:py-20 align-top md:flex-col md:justify-start md:gap-16 sm:align-top"
      >

        {/* TITLE */}

        <motion.div
          className={`md:w-full md:text-center ${
            isAboveLarge ? "mt-14" : ""
          }`}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.8 }}
          variants={{
            hidden: { opacity: 0, x: -50 },
            visible: { opacity: 1, x: 0 },
          }}
        >
          <h2
            className={`font-integralCF ${
              isAboveLarge ? "text-7xl" : "text-4xl"
            } text-center`}
          >
            <span className="border-b-2 inline-block mb-10">LIVE</span>
          </h2>
        </motion.div>

        <div className="flex flex-col items-center mb-10 px-4 text-center">
          <p className="font-monda mb-4">
            Get updates on new shows, new music, and more.
          </p>
          <a href={FOLLOW_URL} target="_blank" rel="noopener noreferrer">
            <button className="bg-rust hover:bg-rustDeep text-coolPaper font-bold uppercase tracking-wide text-sm py-3 px-6 rounded transition-colors">
              Follow Strange Lot
            </button>
          </a>
        </div>

        <LiveShows />

        <div className="w-full max-w-6xl px-4">
          {/* Event list/tabs and the follow section are hidden via CSS
              (.bit-event-lists, .bit-nav-bar-container, .bit-follow-section-wrapper
              in index.css) - LiveShows and the Follow button above render that
              same data ourselves with full styling control and independent
              placement. This instance now only drives Play My City. */}
          <div
            className="bit-widget-initializer"
            data-artist-name="id_10733964"
            data-app-id="996ff3e9d664fe9051bcd36a756f22b4"
            data-widget-width="100%"
            data-background-color="rgba(20,26,30,1)"
            data-text-color="#EDF1F1"
            data-font="Impact"
            data-button-label-capitalization="uppercase"
            data-display-track-button="false"
            data-bit-logo-color="rgba(237,241,241,0.6)"
          ></div>
        </div>
      </div>
      <section id="contact" className="w-full bg-coolLight text-coolInk py-14">
        <div>
          <div className="flex flex-col items-center">
            <p className="font-monda text-center mb-1">Contact</p>
            <p className="font-monda"> MGMT@STRANGELOT.COM</p>
            {/* <p className="font-monda">BOOKING: MICKEY@IVYAGENCYWORLDWIDE.COM</p> */}
          </div>
        </div>
      </section>
    </section>
  );
};

export default Store;
